import { LucideIcon } from "lucide-react"
import { Button } from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { Permission, Workspace, Connection } from "@/types"
import { PermissionButton } from "@/components/Auth"

export interface OnboardingStep {
  title: string
  description?: string
}

export interface OnboardingActionPermission {
  /** The permission required to enable this action */
  required: Permission
  /** Workspace to check permissions against */
  workspace?: Workspace
  /** Connection to check permissions against */
  connection?: Connection
  /** Tooltip message to show when user lacks permission */
  disabledTooltip?: string
}

export interface OnboardingAction {
  label: string
  onClick: () => void
  /** Optional permission configuration. When provided, the action will be permission-controlled. */
  permission?: OnboardingActionPermission
}

export interface OnboardingEmptyStateProps {
  icon: LucideIcon
  title: string
  description: string
  primaryAction?: OnboardingAction
  secondaryAction?: OnboardingAction
  steps?: OnboardingStep[]
  learnMoreUrl?: string
  learnMoreLabel?: string
  className?: string
  children?: React.ReactNode
}

const OnboardingEmptyState = ({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  steps,
  learnMoreUrl,
  learnMoreLabel = "Learn more",
  className,
  children,
}: OnboardingEmptyStateProps) => {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-12 px-6 text-center",
        className
      )}
    >
      <div className="mb-4 rounded-full bg-primary/10 p-4">
        <Icon className="h-8 w-8 text-primary" />
      </div>

      <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
        {title}
      </h3>

      <p className="mb-6 max-w-md text-sm text-slate-600 dark:text-slate-400">
        {description}
      </p>

      {steps && steps.length > 0 && (
        <div className="mb-6 w-full max-w-md">
          <ol className="space-y-3 text-left">
            {steps.map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {step.title}
                  </p>
                  {step.description && (
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {step.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {children}

      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        {primaryAction &&
          (primaryAction.permission ? (
            <PermissionButton
              permission={primaryAction.permission.required}
              workspace={primaryAction.permission.workspace}
              connection={primaryAction.permission.connection}
              disabledTooltip={primaryAction.permission.disabledTooltip}
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </PermissionButton>
          ) : (
            <Button onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          ))}
        {secondaryAction &&
          (secondaryAction.permission ? (
            <PermissionButton
              variant="outline"
              permission={secondaryAction.permission.required}
              workspace={secondaryAction.permission.workspace}
              connection={secondaryAction.permission.connection}
              disabledTooltip={secondaryAction.permission.disabledTooltip}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </PermissionButton>
          ) : (
            <Button variant="outline" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          ))}
      </div>

      {learnMoreUrl && (
        <a
          href={learnMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 text-sm text-primary hover:underline"
        >
          {learnMoreLabel} &rarr;
        </a>
      )}
    </div>
  )
}

export default OnboardingEmptyState
